import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import pLimit from "p-limit";

const EventContext = createContext();

export function useEventContext() {
  return useContext(EventContext);
}

// Utility function to extract shortened event ID from URL
const getShortenedEventIdFromUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(/\/event\/([a-zA-Z0-9]{5})/); // Match alphanumeric IDs
  return match ? match[1] : null; // Return the shortened ID (first 5 characters)
};

export function EventProvider({ children }) {
  // Original state for all events (kept for potential future use)
  const [fullEventId, setFullEventId] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [allEventInstances, setAllEventInstances] = useState({});
  // Current selected event data
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventInstances, setEventInstances] = useState([]);
  const [priceDetails, setPriceDetails] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [customItems, setCustomItems] = useState({});
  const [studentPrices, setStudentPrices] = useState({});
  const [generalAdmTicketTypeId, setGeneralAdmTicketTypeIds] = useState({});
  const [studentTicketTypeId, setStudentTicketTypeId] = useState({});
  const [ticketTypeNames, setTicketTypeNames] = useState({});
  const [availableStatus, setAvailableStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get shortened event ID from URL (using parent window for iframe context)
  const shortenedEventId = useMemo(() => {
    const currentUrl = window.parent.location.href;
    // Sample URL
    //const currentUrl = "https://jazzforumarts.org/event/2001a/";
    return getShortenedEventIdFromUrl(currentUrl);
  }, []);

  useEffect(() => {
    const fetchEventData = async () => {
      setLoading(true);
      setError(null);

      try {
        const baseUrl = "https://events.jazzforumarts.org/jazzforumarts/api/v3";

        // Step 1: Fetch all events to find the matching one
        const eventsResponse = await fetch(`${baseUrl}/events?onSale=true`);
        const events = await eventsResponse.json();
        setAllEvents(events);

        if (!shortenedEventId) {
          setError("No event ID found in URL");
          return;
        }

        // Step 2: Find the specific event by shortened ID
        const matchingEvents = events.filter((event) =>
          event.id.toLowerCase().startsWith(shortenedEventId.toLowerCase())
        );

        if (matchingEvents.length === 0) {
          setError(`No event found for ID: ${shortenedEventId}`);
          return;
        }

        if (matchingEvents.length > 1) {
          console.warn(
            `Multiple events found for ID ${shortenedEventId}:`,
            matchingEvents.map((e) => e.id)
          );
        }

        const targetEvent = matchingEvents[0];
        setSelectedEvent(targetEvent);
        setFullEventId(targetEvent.id);

        // Step 3: Fetch instances only for the selected event
        const todayDate = new Date(new Date().toISOString().substring(0, 10));
        const instancesResponse = await fetch(
          `${baseUrl}/events/${targetEvent.id}/instances`
        );
        const instances = await instancesResponse.json();

        const futureInstances = instances.filter(
          (inst) =>
            inst.isOnSale && inst.start && new Date(inst.start) >= todayDate
        );

        if (futureInstances.length === 0) {
          setError("No available instances found for this event");
          return;
        }

        setEventInstances(futureInstances);
        const instanceIds = futureInstances.map((inst) => inst.id);

        // Step 4: Fetch pricing and availability data only for selected event instances
        const limit = pLimit(10);

        const priceFetches = instanceIds.map((id) =>
          limit(() =>
            fetch(
              `${baseUrl}/instances/${id}/price-list?$expand=prices/customItems`
            )
              .then((res) => res.json())
              .then((data) => ({ id, data }))
          )
        );

        const availFetches = instanceIds.map((id) =>
          limit(() =>
            fetch(
              `${baseUrl}/instances/${id}/status?includeLockInformation=true&includeChildPlans=true`
            )
              .then((res) => res.json())
              .then((data) => ({ id, data }))
          )
        );

        const [priceResults, availResults] = await Promise.all([
          Promise.allSettled(priceFetches),
          Promise.allSettled(availFetches),
        ]);

        // Step 5: Process the results
        const updatedEventPrices = {},
          updatedCustomItems = {},
          updatedStudentPrices = {},
          updatedAdmTicketTypeIds = {},
          updatedStuTicketTypeIds = {},
          updatedTicketTypeNames = {},
          updatedTicketData = {},
          updatedAvailableStatus = {};

        priceResults.forEach((res) => {
          if (res.status === "fulfilled" && res.value.data?.prices) {
            const { id, data } = res.value;
            const std = data.prices.find((p) => p.isBandDefault);
            if (std) {
              updatedEventPrices[id] = std.amount;
              updatedCustomItems[id] = std.customItems.map((c) => c.amount);
              updatedTicketTypeNames[id] = std.ticketType.name;
            }
            const stu = data.prices.find(
              (p) => p.ticketType.name === "Student (must present ID at door)"
            );
            if (stu) {
              updatedStudentPrices[id] = stu.amount;
              updatedStuTicketTypeIds[id] = stu.ticketType.id;
            }
            const gen = data.prices.find(
              (p) => p.ticketType.name === "General Adm"
            );
            if (gen) {
              updatedAdmTicketTypeIds[id] = gen.ticketType.id;
            }
            updatedTicketData[id] = data.prices.map((p) => ({
              ticketTypeName: p.ticketType.name,
              ticketTypeId: p.ticketType.id,
              ticketAmount: p.amount,
              commissionAmount: p.customItems[0]?.amount,
            }));
          }
        });

        availResults.forEach((res) => {
          if (res.status === "fulfilled") {
            updatedAvailableStatus[res.value.id] =
              res.value.data?.available ?? 0;
          }
        });

        setPriceDetails(updatedEventPrices);
        setCustomItems(updatedCustomItems);
        setStudentPrices(updatedStudentPrices);
        setGeneralAdmTicketTypeIds(updatedAdmTicketTypeIds);
        setStudentTicketTypeId(updatedStuTicketTypeIds);
        setTicketTypeNames(updatedTicketTypeNames);
        setTicketData(updatedTicketData);
        setAvailableStatus(updatedAvailableStatus);
      } catch (err) {
        console.error("Error in data fetching pipeline", err);
        setError("Failed to fetch event data");
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [shortenedEventId]);

  // Memoized custom event details - now only for the selected event
  const customEventDetails = useMemo(() => {
    if (!selectedEvent || !eventInstances.length) return [];

    return eventInstances.map((instance) => ({
      start: instance.start,
      isOnSale: instance.isOnSale,
      planId: instance.planId,
      instanceId: instance.id,
      name: selectedEvent.name,
      id: selectedEvent.id,
      ...selectedEvent,
      prices: priceDetails[instance.id],
      commission: customItems[instance.id],
      studentPrice: studentPrices[instance.id],
      studentTicketTypeId: studentTicketTypeId[instance.id],
      generalTicketTypeId: generalAdmTicketTypeId[instance.id],
      ticketTypeName: ticketTypeNames[instance.id],
      ticketData: ticketData[instance.id],
      availableStatus: availableStatus[instance.id],
    }));
  }, [
    selectedEvent,
    eventInstances,
    priceDetails,
    customItems,
    studentPrices,
    studentTicketTypeId,
    generalAdmTicketTypeId,
    ticketTypeNames,
    ticketData,
    availableStatus,
  ]);

  // Get the primary event details (first instance or event itself)
  const primaryEventDetails = useMemo(() => {
    if (customEventDetails.length > 0) {
      return customEventDetails[0];
    }
    return selectedEvent;
  }, [customEventDetails, selectedEvent]);

  //console.log(eventInstances)

  return (
    <EventContext.Provider
      value={{
        selectedEvent,
        fullEventId,
        customEventDetails,
        eventInstances,
        loading,
        priceDetails,
        ticketData,
        studentPrices,
        generalAdmTicketTypeId,
        studentTicketTypeId,
        ticketTypeNames,
        availableStatus,
        customItems,
        shortenedEventId, // Make sure this is named correctly
      }}
    >
      {children}
    </EventContext.Provider>
  );
}
